import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { hasFeature, featureLimit, requiredPlan, canWrite, isOwner } from "@/lib/plans";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("aulia_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("aulia_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password, meta = {}) => {
    const { data } = await api.post("/auth/login", { email, password, ...meta });
    localStorage.setItem("aulia_token", data.token);
    setUser(data.user);
    return data.user;
  };

  /** Connexion directe après acceptation d'une invitation (token déjà émis par l'API). */
  const loginWithToken = useCallback((token, userData) => {
    localStorage.setItem("aulia_token", token);
    setUser(userData || null);
  }, []);

  const logout = () => {
    localStorage.removeItem("aulia_token");
    setUser(null);
    window.location.href = "/login";
  };

  const patchUser = useCallback((partial) => {
    setUser((prev) => (prev ? { ...prev, ...partial } : prev));
  }, []);

  // Helpers offre / rôle (lecture seule — la règle est appliquée côté serveur)
  const can = useCallback((code) => hasFeature(user, code), [user]);
  const limit = useCallback((code) => featureLimit(user, code), [user]);
  const planFor = useCallback((code) => requiredPlan(user, code), [user]);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    loginWithToken,
    logout,
    refresh: fetchMe,
    patchUser,
    can,
    limit,
    planFor,
    role: user?.role || "owner",
    canWrite: canWrite(user),
    isOwner: isOwner(user),
    account: user?.account || null,
    usage: user?.usage || null,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [user, loading, fetchMe, patchUser, can, limit, planFor, loginWithToken]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
